import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { GenericValidationsService } from 'src/app/Service/generic-validations.service';
import { SevaProfileService } from 'src/app/Service/seva-profile.service';
import { TranslationService } from 'src/app/Service/translation.service';
import { CONSTANTS } from '../../../../Core/Constant';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-medical-history',
  templateUrl: './medical-history.component.html',
  styleUrls: ['./medical-history.component.scss']
})
export class MedicalHistoryComponent {


  @Input() applicantData: any; // Accept data from parent component
  @Input() isPreviewMode: boolean = false; // Flag to determine behavior
  @Output() formGroupChange = new EventEmitter<FormGroup>();
  @Output() nextStep = new EventEmitter<void>();
  @Output() previous = new EventEmitter<void>();

  medicalHistoryForm!: FormGroup;
  submitted = false;
  SaveMode = CONSTANTS.SAVE_MODE;
  isJsonShow = environment.isJsonShow;  // Set isJsonShow from the environment
  weights: any[] = [];
  heightFeet: any[] = [];
  heightInch: any[] = [];
  selectedLanguage: string = 'en'; // Default language
  isEditable: boolean = true; // Flag to track if the form is editable

  constructor(
    private toastr: ToastrService, 
    private api: SevaProfileService, 
    private fb: FormBuilder, 
    public genericValidations: GenericValidationsService,
    private translationService: TranslationService
  ) { }



  ngOnInit(): void {
    // Load saved language preference
    this.selectedLanguage = this.translationService.getCurrentLanguage();

    if (!this.isPreviewMode) {
      // Execute only when not in preview mode
      this.initializeForm();
      this.getApplicantData();

      const minWeight = 5; // Starting from 5 kg
      const maxWeight = 200; // Example maximum weight

      // Generate weight range
      for (let weight = minWeight; weight <= maxWeight; weight++) {
        this.weights.push(weight.toString());
      }

      // Generate height feet range (0-10)
      for (let feet = 0; feet <= 10; feet++) {
        this.heightFeet.push(feet.toString());
      }

      // Generate height inches range (0-11)
      for (let inch = 0; inch <= 11; inch++) {
        this.heightInch.push(inch.toString());
      }
    }
  }

  // Get translation for current language
  getTranslation(key: string): string {
    return this.translationService.getTranslation(key);
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['applicantData'] && this.applicantData) {
      this.initializeForm();
      this.medicalHistoryForm.patchValue(this.applicantData);
      this.medicalHistoryForm.disable();
    }
  }




  getApplicantData() {
    this.api.apiCall('MedicalHistory/GetMedicalHistory', 'GET').subscribe((response: any) => {
      if (response.Data && Object.keys(response.Data).length > 0) {
        this.genericValidations.setProgressPercentage(response.Data.CompletionPercentage); // Example: Set progress to 50%

        // Store IsEditable flag from response
        this.isEditable = response.Data.IsEditable !== false; // Default to true if not provided

        // Disable form if not editable
        if (!this.isEditable) {
          this.medicalHistoryForm.disable();
        } else {
          this.medicalHistoryForm.enable();
        }

        this.medicalHistoryForm.patchValue(response.Data);
      }
    })
  }


  initializeForm() {
    this.medicalHistoryForm = this.fb.group({
      HeightFeet: ['', [Validators.required]],
      HeightInch: ['', [Validators.required]],
      Weight: ['', Validators.required],
      IsTyphoidFever: [null, Validators.required],
      TyphoidFeverDetails: ['', Validators.required],
      IsTuberculosis: [null, Validators.required],
      TuberculosisDetails: ['', Validators.required],
      IsDepression: [null, Validators.required],
      DepressionDetails: ['', Validators.required],
      IsPsychiatricDisorder: [null, Validators.required],
      PsychiatricDisorderDetails: ['', Validators.required],
      IsBloodPressureProblem: [null, Validators.required],
      BpRange: ['', Validators.required],
      IsBloodSugarProblem: [null, Validators.required],
      BloodSugarRange: ['', Validators.required],
      IsBrainStroke: [null, Validators.required],
   
      BrainStrokeDetails: ['', Validators.required],
      IsHiv: [null, Validators.required],
      HivConditionDetails: ['',  Validators.required],
      IsParasites: [null, Validators.required],
      ParasitesDetails: ['', Validators.required],
      IsHepatitis: [null, Validators.required],
      HepatitisDetails: ['', Validators.required],
      IsHeartProblem: [null, Validators.required],
      HeartProblemDetails: ['', Validators.required],
      IsCancer: [null, Validators.required],
      CancerDetails: ['', Validators.required],
      IsAsthma: [null, Validators.required],
      AsthmaDetails: ['', Validators.required],
      IsLungProblem: [null, Validators.required],
      LungProblemDetails: ['', Validators.required],
      IsVisionProblem: [null, Validators.required],
      VisionProblemDetails: ['', Validators.required],
      IsOtherMedicalCondition: [null, Validators.required],
      IsRegularMedication: [null, Validators.required],
      OtherMedicalConditionDetails: ['', Validators.required],
      Medications: ['', Validators.required],
      SaveMode: ['', [Validators.required,]],

      // Add controls for other conditions similarly
    });


    const controlMappings = {
      'IsRegularMedication': ['Medications'],
      'IsBrainStroke': ['BrainStrokeDetails'],
      'IsTyphoidFever': ['TyphoidFeverDetails'],
      'IsTuberculosis': ['TuberculosisDetails'],
      'IsDepression': ['DepressionDetails'],
      'IsPsychiatricDisorder': ['PsychiatricDisorderDetails'],
      'IsBloodPressureProblem': ['BpRange'],
      'IsBloodSugarProblem': ['BloodSugarRange'],
      'IsHiv': ['HivConditionDetails'],
      'IsParasites': ['ParasitesDetails'],
      'IsHepatitis': ['HepatitisDetails'],
      'IsHeartProblem': ['HeartProblemDetails'],
      'IsCancer': ['CancerDetails'],
      'IsAsthma': ['AsthmaDetails'],
      'IsLungProblem': ['LungProblemDetails'],
      'IsVisionProblem': ['VisionProblemDetails'],
      'IsOtherMedicalCondition': ['OtherMedicalConditionDetails']
    }


    // Call the service with the isShowOn parameter set to true or false as needed
    this.genericValidations.setupFormListeners(this.medicalHistoryForm, controlMappings, true);

    // Emit the form group to parent component
    this.formGroupChange.emit(this.medicalHistoryForm);
  }


  onSubmit(Mode: number): void {


    // this.medicalHistoryForm.patchValue({
    //   SaveMode: Mode
    // })

    if (!this.medicalHistoryForm.valid && Mode == this.SaveMode.SAVE) {
      this.submitted = true;

      // Add 'submitted' class manually to all invalid controls
      Object.keys(this.medicalHistoryForm.controls).forEach((field) => {
        const control = this.medicalHistoryForm.get(field);
        if (control && control.invalid) {
          control.markAsTouched(); // Ensure ng-touched is triggered
          const element = document.getElementById(field);
          if (element) {
            element.classList.add('submitted'); // Add 'submitted' class to invalid elements
          }
        }
      });
      this.toastr.warning(this.getTranslation('validation.completeAllFields'));


      return
    }

    const cleanedValues = this.genericValidations.removeWhitespaceFromValues(this.medicalHistoryForm.value);
    // const cleanedValues = this.genericValidations.removeWhitespaceFromValues(formValues);
    this.addUpdate(cleanedValues, Mode);


  }

  addUpdate(reqBoby: any, Mode: number) {
    reqBoby.SaveMode = Mode;
    this.api.apiCall('MedicalHistory/SaveMedicalHistory', 'POST', reqBoby).subscribe((response: any) => {
      console.log('POST response:', response);
      this.toastr.success(response?.Message);
      if (Mode == this.SaveMode.SAVE) {
        this.nextStep.emit();
      } else if (Mode == this.SaveMode.DRAFT) {
        this.getApplicantData();
      }
    });
  }


  goToBack(): void {
    this.previous.emit();
  }


}
